// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate, Navigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import NavbarNew from './components/layout/NavbarNew';
import FooterNew from './components/layout/FooterNew';
import { SkipLink } from './components/common/Accessibility';
import { UserProvider, useUser } from './context/UserContext';
import { CreditProvider } from './context/CreditContext';
import ScrollToTop from './components/common/ScrollToTop';
import RouteChangeTracker from './components/common/RouteChangeTracker';
import { PageContentProvider } from './context/PageContentContext';
import { TopAdProvider } from './context/TopAdContext';
import AutoPageSEO from './components/AutoPageSEO';
import { ChatWidget } from './components/chatbot';
import WelcomePopup from './components/common/WelcomePopup';
import CookieConsent from './components/common/CookieConsent';
import TopAnnouncementBar from './components/ads/TopAnnouncementBar';

// Design System
import { ThemeProvider } from './design-system';

// Lazy load pages for code splitting - reduces initial bundle size
const HomePageNew = lazy(() => import('./pages/HomePageNew'));
const Listings = lazy(() => import('./pages/Listings'));
const Deals = lazy(() => import('./pages/Deals'));
const Jobs = lazy(() => import('./pages/Jobs'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const TotalListings = lazy(() => import('./pages/TotalListings'));
const NotFound = lazy(() => import('./pages/NotFound'));
const BusinessDetailsNew = lazy(() => import('./pages/BusinessDetailsNew'));
const JobDetailsNew = lazy(() => import('./pages/JobDetailsNew'));
const JobApplyPage = lazy(() => import('./pages/JobApplyPage'));
const DealDetailsNew = lazy(() => import('./pages/DealDetailsNew'));
const Classifieds = lazy(() => import('./pages/Classifieds'));
const ClassifiedDetails = lazy(() => import('./pages/ClassifiedDetails'));
const ProductSearchPage = lazy(() => import('./pages/ProductSearchPage'));

// Heavy pages - lazy load with higher priority for code splitting
const AITools = lazy(() => import(/* webpackChunkName: "aitools" */ './pages/AITools'));
const LearningHub = lazy(() => import(/* webpackChunkName: "learninghub" */ './pages/LearningHub'));
const PrintableReport = lazy(() => import(/* webpackChunkName: "learninghub" */ './components/learninghub/PrintableReport'));

// Real Estate pages
const RealEstate = lazy(() => import(/* webpackChunkName: "realestate" */ './pages/RealEstate'));
const PropertyDetail = lazy(() => import(/* webpackChunkName: "realestate" */ './pages/PropertyDetail'));
const AgentDetail = lazy(() => import(/* webpackChunkName: "realestate" */ './pages/AgentDetail'));
const AgencyDetail = lazy(() => import(/* webpackChunkName: "realestate" */ './pages/AgencyDetail'));
const FindAgents = lazy(() => import(/* webpackChunkName: "realestate" */ './pages/FindAgents'));
const FindAgencies = lazy(() => import(/* webpackChunkName: "realestate" */ './pages/FindAgencies'));

// Blog pages
const Blog = lazy(() => import(/* webpackChunkName: "blog" */ './pages/Blog'));
const BlogPost = lazy(() => import(/* webpackChunkName: "blog" */ './pages/BlogPost'));

// City landing pages
const CityListingPage = lazy(() => import('./pages/CityListingPage'));
const CategoryListingPage = lazy(() => import('./pages/CategoryListingPage'));
const HeritageTagListingPage = lazy(() => import('./pages/HeritageTagListingPage'));

// High-intent SEO landing pages (keyword-rich vanity URLs)
const HighIntentLandingPage = lazy(() => import(/* webpackChunkName: "hi-pages" */ './pages/HighIntentLandingPage'));


// Loading spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Loading fallback component for Suspense
const LoadingFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0093E9;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const PageLoader = () => (
  <LoadingFallback>
    <Spinner />
  </LoadingFallback>
);

// App Layout - ensures footer stays at bottom
const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;

  &:focus {
    outline: none;
  }
`;

// Main component with routes wrapped with CreditProvider
const MainWithProviders = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useUser();
  const [welcomePopupVisible, setWelcomePopupVisible] = React.useState(false);
  const [forceShowGuide, setForceShowGuide] = React.useState(false);
  const isDashboard = location.pathname.startsWith('/app') || location.pathname.startsWith('/dashboard');
  const showNavbar = true;
  const showFooter = true;
  // Show "How to Add Your Business" guide to all visitors (authenticated or not)
  const showWelcomePopup = true;

  return (
    <PageContentProvider>
    <TopAdProvider>
    <CreditProvider>
      <AppWrapper>
        {/* Skip Link for accessibility - keyboard users can skip to main content */}
        <SkipLink targetId="main-content" />

        <TopAnnouncementBar />
        {showNavbar && <NavbarNew />}
        <ScrollToTop />
        <RouteChangeTracker />

        <MainContent id="main-content" tabIndex="-1" role="main" aria-label="Main content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePageNew />} />
              <Route path="/all-listings" element={<Listings />} />
              <Route path="/deals-list" element={<Deals />} />
              <Route path="/jobs-list" element={<Jobs />} />
              <Route path="/classifieds" element={<Classifieds />} />

              {/* National directory pages — keep HeritageTagListingPage for these (full feature set) */}
              <Route path="/indian-business-directory-australia" element={<HeritageTagListingPage nationalHeritageSlug="indian" nationalH1="Indian Business Directory Australia" />} />
              <Route path="/pakistani-business-directory-australia" element={<HeritageTagListingPage nationalHeritageSlug="pakistani" nationalH1="Pakistani Business Directory Australia" />} />
              <Route path="/bangladeshi-business-directory-australia" element={<HeritageTagListingPage nationalHeritageSlug="bangladeshi" nationalH1="Bangladeshi Business Directory Australia" />} />

              {/* City, Category & Cuisine Tag landing pages */}
              <Route path="/businesses/:citySlug/:heritageSlug/:categorySlug" element={<HeritageTagListingPage />} />
              <Route path="/businesses/:citySlug/:categorySlug" element={<CityListingPage />} />
              <Route path="/businesses/:citySlug" element={<CityListingPage />} />
              <Route path="/categories/:categorySlug" element={<CategoryListingPage />} />

              {/* Redirects for old/common URLs */}
              <Route path="/deals" element={<Navigate to="/deals-list" replace />} />
              <Route path="/jobs" element={<Navigate to="/jobs-list" replace />} />
              <Route path="/businesses" element={<Navigate to="/all-listings" replace />} />
              <Route path="/listings" element={<Navigate to="/all-listings" replace />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/total-list/:id" element={<TotalListings/>}/>
              <Route path="/deal/:slug" element={<DealDetailsNew />} />
              <Route path="/business/:slug" element={<BusinessDetailsNew />} />
              <Route path="/job/:slug" element={<JobDetailsNew />}/>
              <Route path="/job/:slug/apply" element={<JobApplyPage />}/>
              <Route path="/classified/:slug" element={<ClassifiedDetails />} />
              <Route path="/ai-tools/*" element={<AITools />} />
              <Route path="/learning-hub" element={<LearningHub />} />
              <Route path="/learning-hub/progress-report" element={<PrintableReport />} />
              <Route path="/product-search" element={<ProductSearchPage />} />

              {/* Real Estate Routes */}
              <Route path="/realestate" element={<RealEstate />} />
              <Route path="/realestate/property/:slug" element={<PropertyDetail />} />
              <Route path="/realestate/find-agents" element={<FindAgents />} />
              <Route path="/realestate/find-agencies" element={<FindAgencies />} />
              <Route path="/realestate/agents/:slug" element={<AgentDetail />} />
              <Route path="/realestate/agencies/:slug" element={<AgencyDetail />} />

              {/* Blog routes */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* Catch-all for generated high-intent keyword combo pages.
                  Must be last — all specific routes above take precedence.
                  Django backend controls which slugs are valid via HIGH_INTENT_PAGES.
                  Unknown slugs get noindex + "be the first to list" CTA from the component. */}
              <Route path="/:slug" element={<HighIntentLandingPage />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          {/* Global FAQ block — auto-renders for any page the backend returns FAQs for.
              Zero per-page code needed. Add new paths to get_page_content_for_path()
              in config/views.py and they automatically get FAQ here. */}
          <AutoPageSEO />
        </MainContent>

        {showFooter && <FooterNew />}

        {/* AI Chatbot Widget - hidden when welcome popup is showing */}
        {!welcomePopupVisible && <ChatWidget onMobileNudgeClick={() => setForceShowGuide(true)} />}

        {/* "How to Add Your Business" guide — shown once to non-authenticated visitors */}
        {showWelcomePopup && (
          <WelcomePopup
            isAuthenticated={isAuthenticated}
            onVisibilityChange={setWelcomePopupVisible}
            forceShow={forceShowGuide}
            onForceShowHandled={() => setForceShowGuide(false)}
          />
        )}

        {/* Cookie Consent Banner - disabled (AU has no GDPR requirement) */}
        {/* <CookieConsent /> */}
      </AppWrapper>
    </CreditProvider>
    </TopAdProvider>
    </PageContentProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <UserProvider>
        <Router>
          <MainWithProviders />
        </Router>
      </UserProvider>
    </ThemeProvider>
  );
};

export default App;
